import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'src/components';
import { MemsApi } from 'src/api';
import { Mem, PictureMetadata } from 'src/api/learningSessions/types';
import { resizeImage } from 'src/utils/resizeImage';
import { sharedMemMessages } from '../sharedMemMessages';
import {
  BOLD_MARKER,
  DEFAULT_PICTURE_METADATA,
  ITALIC_MARKER,
  applyMarkupToSelection,
  hasMarkupFormat,
} from '../memTextFormat';
import { MemMarkdown } from '../MemMarkdown';
import { AiImageLoadingBar } from './AiImageLoadingBar';
import * as S from './styles';

const MAX_MEM_LENGTH = 280;
const MIN_FONT_SIZE = 0.75;
const MAX_FONT_SIZE = 2.5;
const FONT_SIZE_STEP = 0.25;
const AI_POLL_INTERVAL_MS = 3000;
const AI_LIMIT_ERROR = 'ai_image_generation_limit_exceeded';

const msgs = defineMessages({
  createButton: { id: 'create_mem_button_text', defaultMessage: 'Create a mem' },
  createTitle: { id: 'create_mem_modal_title', defaultMessage: 'Create a mem' },
  placeholder: {
    id: 'create_mem_textarea_placeholder',
    defaultMessage: 'What helps you remember this word?',
  },
  submit: { id: 'create_mem_submit', defaultMessage: 'Create' },
  uploadImageTab: { id: 'mem_upload_image_tab', defaultMessage: 'Upload image' },
  aiImageTab: { id: 'cwl_generate_with_ai', defaultMessage: 'Generate with AI' },
  orSeparator: { id: 'daily_activities_interstitial_or' },
  uploadImageHint: { id: 'mem_upload_image_hint', defaultMessage: 'Click to upload an image' },
  addImage: { id: 'create_mem_add_image_label', defaultMessage: 'Add image' },
  generateAiImage: { id: 'mem_generate_ai_image', defaultMessage: 'Generate' },
  generatingAiImage: {
    id: 'mem_ai_generating',
    defaultMessage: 'Generating image…',
  },
  aiLimitExceeded: {
    id: 'mem_ai_image_limit_exceeded',
    defaultMessage: 'Daily AI image limit reached ({count}/day). Try again tomorrow.',
  },
  aiGenerationFailed: {
    id: 'mem_ai_image_generation_failed',
    defaultMessage: 'Image generation failed. Please try again.',
  },
  submitFailed: { id: 'something_went_wrong' },
});

// Mems are always created privately; there is no visibility choice.
const IS_PUBLIC_ON_CREATE = false;

export type CreateMemOnCreated = (mem: Mem, opts: { aiPending: boolean }) => void;

export interface CreateMemButtonProps {
  learnableId: number;
  onCreated: CreateMemOnCreated;
  onCreateClick?: () => void;
  itemValue?: string | null;
  definitionValue?: string | null;
  targetLanguageName?: string;
  sourceLanguageName?: string;
}

export const CreateMemButton: FC<CreateMemButtonProps> = ({
  learnableId,
  onCreated,
  onCreateClick,
  itemValue,
  definitionValue,
  targetLanguageName,
  sourceLanguageName,
}) => {
  const intl = useIntl();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftMemIdRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const previewRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startFmtX: number;
    startFmtY: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const cropImgRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const cropStartRef = useRef<{ startX: number; startY: number } | null>(null);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    if (pendingSelectionRef.current !== null && textAreaRef.current) {
      textAreaRef.current.setSelectionRange(
        pendingSelectionRef.current.start,
        pendingSelectionRef.current.end,
      );
      pendingSelectionRef.current = null;
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [text, setText] = useState('');
  const [pictureMetadata, setPictureMetadata] = useState<PictureMetadata>({
    ...DEFAULT_PICTURE_METADATA,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [imageTab, setImageTab] = useState<'upload' | 'ai'>('upload');
  const [aiPrompt, setAiPrompt] = useState('');

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [rawImagePreview, setRawImagePreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [imageAspectRatio, setImageAspectRatio] = useState<string | null>(null);

  const [isImageSectionOpen, setIsImageSectionOpen] = useState(false);

  const [cropMode, setCropMode] = useState(false);
  const [cropDrawing, setCropDrawing] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);

  const [draftMemId, setDraftMemId] = useState<number | null>(null);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiGeneratedImageUrl, setAiGeneratedImageUrl] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const defaultAiPrompt = [
    itemValue && `Generate a funny visual mnemonic for the word "${itemValue}"`,
    targetLanguageName && `in ${targetLanguageName}`,
    definitionValue && `which means "${definitionValue}"`,
    sourceLanguageName && `in ${sourceLanguageName}`,
    itemValue &&
      `The image should incorporate words or visuals that sound like "${itemValue}" to aid memory.`,
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current || !previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      const newX = Math.max(
        0,
        Math.min(100, dragRef.current.startFmtX - (deltaX / rect.width) * 100),
      );
      const newY = Math.max(
        0,
        Math.min(100, dragRef.current.startFmtY - (deltaY / rect.height) * 100),
      );
      setPictureMetadata(prev => ({ ...prev, imageX: newX, imageY: newY }));
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!cropStartRef.current || !cropContainerRef.current) return;
      const rect = cropContainerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      const { startX, startY } = cropStartRef.current;
      setCropDrawing({
        x: Math.min(x, startX),
        y: Math.min(y, startY),
        w: Math.abs(x - startX),
        h: Math.abs(y - startY),
      });
    };
    const handleMouseUp = () => {
      cropStartRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handlePreviewMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startFmtX: pictureMetadata.imageX ?? 50,
      startFmtY: pictureMetadata.imageY ?? 50,
    };
    setIsDragging(true);
  };

  const handleCropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cropContainerRef.current) return;
    const rect = cropContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cropStartRef.current = { startX: x, startY: y };
    setCropDrawing({ x, y, w: 0, h: 0 });
  };

  const handleApplyCrop = () => {
    if (!cropDrawing || cropDrawing.w < 10 || cropDrawing.h < 10) return;
    const img = cropImgRef.current;
    const container = cropContainerRef.current;
    if (!img || !container) return;

    const containerRect = container.getBoundingClientRect();
    const naturalAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerRect.width / containerRect.height;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (naturalAspect > containerAspect) {
      renderedWidth = containerRect.width;
      renderedHeight = containerRect.width / naturalAspect;
      offsetX = 0;
      offsetY = (containerRect.height - renderedHeight) / 2;
    } else {
      renderedWidth = containerRect.height * naturalAspect;
      renderedHeight = containerRect.height;
      offsetX = (containerRect.width - renderedWidth) / 2;
      offsetY = 0;
    }

    const scaleX = img.naturalWidth / renderedWidth;
    const scaleY = img.naturalHeight / renderedHeight;

    const adjustedCropX = cropDrawing.x - offsetX;
    const adjustedCropY = cropDrawing.y - offsetY;

    const naturalCropX = Math.max(0, Math.round(adjustedCropX * scaleX));
    const naturalCropY = Math.max(0, Math.round(adjustedCropY * scaleY));
    const naturalCropW = Math.max(1, Math.round(cropDrawing.w * scaleX));
    const naturalCropH = Math.max(1, Math.round(cropDrawing.h * scaleY));

    const canvas = document.createElement('canvas');
    canvas.width = naturalCropW;
    canvas.height = naturalCropH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      img,
      naturalCropX,
      naturalCropY,
      naturalCropW,
      naturalCropH,
      0,
      0,
      naturalCropW,
      naturalCropH,
    );
    canvas.toBlob(
      blob => {
        if (!blob) return;
        setCroppedImageBlob(blob);
        const croppedUrl = URL.createObjectURL(blob);
        setImagePreview(croppedUrl);

        setImageAspectRatio(`${naturalCropW} / ${naturalCropH}`);

        setPictureMetadata(prev => ({ ...prev, imageX: 50, imageY: 50 }));
        setCropMode(false);
        setCropDrawing(null);
      },
      'image/jpeg',
      0.9,
    );
  };

  const resetForm = () => {
    setText('');
    setSubmitError(null);
    setPictureMetadata({ ...DEFAULT_PICTURE_METADATA });
    setImageTab('upload');
    setImageFile(null);
    setRawImagePreview(null);
    setImagePreview(null);
    setCroppedImageBlob(null);
    setCropMode(false);
    setCropDrawing(null);
    setAiPrompt('');
    setImageAspectRatio(null);
    setIsImageSectionOpen(false);
    setDraftMemId(null);
    setIsGeneratingAi(false);
    setAiGeneratedImageUrl(null);
    setAiError(null);
  };

  const handleClose = () => {
    const draftId = draftMemIdRef.current;
    draftMemIdRef.current = null;
    setIsModalOpen(false);
    resetForm();
    if (draftId !== null) {
      void MemsApi.deleteMem(draftId).catch(() => {
        // silently fail — draft cleanup is best-effort
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setCroppedImageBlob(null);
    setCropDrawing(null);
    setImageAspectRatio(null);
    setPictureMetadata(prev => ({ ...prev, imageX: 50, imageY: 50 }));
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target?.result as string;
      setRawImagePreview(dataUrl);
      setImagePreview(dataUrl);
      const img = new window.Image();
      img.onload = () => setImageAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const uploadImageReady = imageTab === 'upload' && !!imagePreview;
  const aiImageReady = imageTab === 'ai' && !!aiGeneratedImageUrl;
  const hasReadyImage = uploadImageReady || aiImageReady;
  const isContentMissing = text.trim().length === 0 && !hasReadyImage;
  const isSubmitDisabled =
    isSubmitting || isGeneratingAi || isContentMissing || text.length > MAX_MEM_LENGTH;

  const handleGenerateAi = async () => {
    const prompt = (aiPrompt.trim() || defaultAiPrompt).trim();
    if (!prompt || isGeneratingAi || draftMemId !== null) return;

    setAiError(null);
    setIsGeneratingAi(true);
    try {
      const mem = await MemsApi.createMem(learnableId, {
        ...(text.trim() ? { text: text.trim() } : {}),
        isPublic: IS_PUBLIC_ON_CREATE,
        isSensitive: false,
        aiImagePrompt: prompt,
      });
      draftMemIdRef.current = mem.id;
      setDraftMemId(mem.id);

      const failMessage = intl.formatMessage(msgs.aiGenerationFailed);
      while (draftMemIdRef.current === mem.id && isMountedRef.current) {
        try {
          const result = await MemsApi.pollAiImageStatus(mem.id);
          if (draftMemIdRef.current !== mem.id || !isMountedRef.current) return;
          if (result.status === 'completed' && result.imageUrl) {
            setAiGeneratedImageUrl(result.imageUrl);
            const img = new window.Image();
            img.onload = () => setImageAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
            img.src = result.imageUrl;
            setIsGeneratingAi(false);
            setAiError(null);
            return;
          }
          if (result.status === 'failed') {
            setIsGeneratingAi(false);
            setAiError(failMessage);
            draftMemIdRef.current = null;
            setDraftMemId(null);
            return;
          }
        } catch {
          if (draftMemIdRef.current !== mem.id || !isMountedRef.current) return;
          setIsGeneratingAi(false);
          setAiError(failMessage);
          draftMemIdRef.current = null;
          setDraftMemId(null);
          return;
        }
        await new Promise<void>(resolve => {
          setTimeout(resolve, AI_POLL_INTERVAL_MS);
        });
      }
    } catch (err) {
      setIsGeneratingAi(false);
      const message = err instanceof Error ? err.message : '';
      if (message === AI_LIMIT_ERROR) {
        setAiError(intl.formatMessage(msgs.aiLimitExceeded, { count: 3 }));
      } else {
        setAiError(intl.formatMessage(msgs.aiGenerationFailed));
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (draftMemId !== null && aiGeneratedImageUrl) {
        const confirmedId = draftMemId;
        draftMemIdRef.current = null;
        setDraftMemId(null);

        const updated = await MemsApi.updateMem(confirmedId, {
          text,
          isPublic: IS_PUBLIC_ON_CREATE,
          isSensitive: false,
          pictureMetadata,
        });
        onCreated(
          { ...updated, imageUrl: updated.imageUrl ?? aiGeneratedImageUrl },
          { aiPending: false },
        );
        setIsModalOpen(false);
        resetForm();
        return;
      }

      let imageBlob: Blob | undefined;
      if (imageTab === 'upload') {
        const fileToResize = croppedImageBlob ?? imageFile;
        if (fileToResize) imageBlob = await resizeImage(fileToResize);
      }

      const mem = await MemsApi.createMem(learnableId, {
        text,
        isPublic: IS_PUBLIC_ON_CREATE,
        isSensitive: false,
        ...(imageBlob ? { imageBlob, imageName: imageFile?.name, pictureMetadata } : {}),
      });

      onCreated(mem, { aiPending: false });
      setIsModalOpen(false);
      resetForm();
    } catch {
      // Keep the modal open with the text intact so the mem is not lost.
      setSubmitError(intl.formatMessage(msgs.submitFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyMarkup = (marker: string) => {
    const textarea = textAreaRef.current;
    const selectionStart = textarea?.selectionStart ?? 0;
    const selectionEnd = textarea?.selectionEnd ?? 0;
    const {
      text: newText,
      newSelectionStart,
      newSelectionEnd,
    } = applyMarkupToSelection(text, marker, selectionStart, selectionEnd);
    setText(newText);
    pendingSelectionRef.current = { start: newSelectionStart, end: newSelectionEnd };
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isCmd = e.metaKey;
    const isCtrl = e.ctrlKey;

    if ((isCmd || isCtrl) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      applyMarkup(BOLD_MARKER);
    } else if ((isCmd || isCtrl) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      applyMarkup(ITALIC_MARKER);
    }
  };

  const adjustFontSize = (delta: number) => {
    setPictureMetadata(prev => ({
      ...prev,
      fontSize: Math.max(
        MIN_FONT_SIZE,
        Math.min(MAX_FONT_SIZE, Math.round(((prev.fontSize ?? 1) + delta) * 100) / 100),
      ),
    }));
  };

  const renderImagePreviewControls = (options: {
    showCropReplace: boolean;
    previewUrl: string;
  }) => {
    const hasText = text.trim().length > 0;
    const showHorizontalControls = hasText || options.showCropReplace;

    return (
      <S.ImageControlsWrapper>
        <S.ImageRow>
          <S.ImagePreviewCard
            ref={previewRef}
            $imageUrl={options.previewUrl}
            $imageX={pictureMetadata.imageX ?? 50}
            $imageY={pictureMetadata.imageY ?? 50}
            $isDragging={isDragging}
            $aspectRatio={imageAspectRatio}
            $isCropped={options.showCropReplace && !!croppedImageBlob}
            onMouseDown={handlePreviewMouseDown}
            data-testid="create-mem-image-preview"
          >
            <S.DragHint>Drag to reposition</S.DragHint>
            {hasText && (
              <S.ImagePreviewText
                $align={pictureMetadata.textAlign}
                $position={pictureMetadata.textPosition}
                $fontSize={pictureMetadata.fontSize}
              >
                <MemMarkdown text={text} />
              </S.ImagePreviewText>
            )}
          </S.ImagePreviewCard>

          {hasText && (
            <S.VerticalButtons>
              {(['top', 'middle', 'bottom'] as const).map(p => (
                <S.FormatButton
                  key={p}
                  type="button"
                  $active={pictureMetadata.textPosition === p}
                  onClick={() =>
                    setPictureMetadata(prev => ({
                      ...prev,
                      textPosition:
                        prev.textPosition === p ? DEFAULT_PICTURE_METADATA.textPosition : p,
                    }))
                  }
                  data-testid={`create-mem-position-${p}`}
                >
                  {p === 'top' ? '↑' : p === 'middle' ? '↕' : '↓'}
                </S.FormatButton>
              ))}
            </S.VerticalButtons>
          )}
        </S.ImageRow>

        {showHorizontalControls && (
          <S.HorizontalControls>
            {hasText && (
              <>
                <S.ControlGroup>
                  {(['left', 'center', 'right'] as const).map(a => (
                    <S.FormatButton
                      key={a}
                      type="button"
                      $active={pictureMetadata.textAlign === a}
                      onClick={() =>
                        setPictureMetadata(prev => ({
                          ...prev,
                          textAlign: prev.textAlign === a ? DEFAULT_PICTURE_METADATA.textAlign : a,
                        }))
                      }
                      data-testid={`create-mem-align-${a}`}
                    >
                      {a === 'left' ? '←' : a === 'center' ? '↔' : '→'}
                    </S.FormatButton>
                  ))}
                </S.ControlGroup>

                <S.ControlGroup>
                  <S.FormatButton
                    type="button"
                    onClick={() => adjustFontSize(-FONT_SIZE_STEP)}
                    data-testid="create-mem-font-decrease"
                  >
                    −
                  </S.FormatButton>
                  <S.FontSizeLabel>A</S.FontSizeLabel>
                  <S.FormatButton
                    type="button"
                    onClick={() => adjustFontSize(FONT_SIZE_STEP)}
                    data-testid="create-mem-font-increase"
                  >
                    +
                  </S.FormatButton>
                </S.ControlGroup>
              </>
            )}

            {options.showCropReplace && (
              <S.ControlGroup>
                <S.ImageActionButton
                  type="button"
                  onClick={() => setCropMode(true)}
                  data-testid="create-mem-crop-button"
                >
                  ✂ Crop
                </S.ImageActionButton>
                <S.ImageActionButton
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="create-mem-replace-button"
                >
                  Replace
                </S.ImageActionButton>
              </S.ControlGroup>
            )}
          </S.HorizontalControls>
        )}
      </S.ImageControlsWrapper>
    );
  };

  const canGenerateAi =
    !isGeneratingAi &&
    draftMemId === null &&
    (aiPrompt.trim() || defaultAiPrompt).trim().length > 0;

  return (
    <>
      <S.ButtonCard
        onClick={() => {
          onCreateClick?.();
          setAiPrompt(defaultAiPrompt);
          setIsModalOpen(true);
        }}
        data-testid="create-mem-button"
      >
        <S.PlusIcon>+</S.PlusIcon>
        <S.ButtonLabel>{intl.formatMessage(msgs.createButton)}</S.ButtonLabel>
      </S.ButtonCard>

      <S.WideModal
        isOpen={isModalOpen}
        handleClose={handleClose}
        shouldCloseOnOutsideClick
        shouldCloseOnEsc
        darkCloseIcon
      >
        <S.FormModal>
          <S.ModalTitle>{intl.formatMessage(msgs.createTitle)}</S.ModalTitle>
          {(itemValue || definitionValue) && (
            <S.LearnablePreview>
              {itemValue && <S.LearnableItem>{itemValue}</S.LearnableItem>}
              {definitionValue && <S.LearnableDefinition>{definitionValue}</S.LearnableDefinition>}
            </S.LearnablePreview>
          )}

          <S.FormatToolbar>
            <S.FormatButton
              type="button"
              $active={hasMarkupFormat(text, BOLD_MARKER)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyMarkup(BOLD_MARKER)}
              data-testid="create-mem-bold"
            >
              <strong>{intl.formatMessage(sharedMemMessages.bold)}</strong>
            </S.FormatButton>
            <S.FormatButton
              type="button"
              $active={hasMarkupFormat(text, ITALIC_MARKER)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyMarkup(ITALIC_MARKER)}
              data-testid="create-mem-italic"
            >
              <em>{intl.formatMessage(sharedMemMessages.italic)}</em>
            </S.FormatButton>
          </S.FormatToolbar>

          <S.TextArea
            ref={textAreaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage(msgs.placeholder)}
            data-testid="create-mem-textarea"
          />
          {text && (
            <S.TextPreview data-testid="create-mem-text-preview">
              <MemMarkdown text={text} />
            </S.TextPreview>
          )}
          <S.CharCount $overLimit={text.length > MAX_MEM_LENGTH}>
            {text.length}/{MAX_MEM_LENGTH}
          </S.CharCount>

          <S.ImageSection>
            <S.ImageSectionToggle
              type="button"
              onClick={() => setIsImageSectionOpen(v => !v)}
              aria-expanded={isImageSectionOpen}
              data-testid="create-mem-image-toggle"
            >
              <span>{intl.formatMessage(msgs.addImage)}</span>
              <S.ToggleChevron $open={isImageSectionOpen}>▾</S.ToggleChevron>
            </S.ImageSectionToggle>

            <S.ImageSectionGrid
              $open={isImageSectionOpen}
              inert={isImageSectionOpen ? undefined : true}
            >
              <S.ImageSectionInner $open={isImageSectionOpen}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileChange}
                  data-testid="create-mem-file-input"
                />

                <S.ImageSourceColumns>
                  <S.ImageSourcePanel
                    $active={imageTab === 'upload'}
                    onClick={() => {
                      if (imageTab === 'upload') {
                        fileInputRef.current?.click();
                      } else {
                        setImageTab('upload');
                      }
                    }}
                    data-testid="create-mem-tab-upload"
                  >
                    <S.ImageSourceTitle>
                      {intl.formatMessage(msgs.uploadImageTab)}
                    </S.ImageSourceTitle>
                    <S.ImageSourceBody
                      $active={imageTab === 'upload'}
                      onClick={e => {
                        if (imageTab === 'upload') e.stopPropagation();
                      }}
                    >
                      {!imagePreview && (
                        <S.ImageUploadArea
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="create-mem-upload-area"
                        >
                          {intl.formatMessage(msgs.uploadImageHint)}
                        </S.ImageUploadArea>
                      )}

                      {imagePreview && (
                        <>
                          {cropMode ? (
                            <>
                              <S.CropContainer
                                ref={cropContainerRef}
                                onMouseDown={handleCropMouseDown}
                                data-testid="create-mem-crop-container"
                              >
                                <S.CropSourceImg
                                  ref={cropImgRef}
                                  src={rawImagePreview ?? imagePreview}
                                  alt=""
                                  draggable={false}
                                />
                                {cropDrawing && cropDrawing.w > 4 && cropDrawing.h > 4 && (
                                  <S.CropSelection
                                    $x={cropDrawing.x}
                                    $y={cropDrawing.y}
                                    $w={cropDrawing.w}
                                    $h={cropDrawing.h}
                                  />
                                )}
                              </S.CropContainer>
                              <S.CropActionRow>
                                <S.CropActionButton
                                  type="button"
                                  onClick={() => {
                                    setCropMode(false);
                                    setCropDrawing(null);
                                  }}
                                >
                                  Cancel
                                </S.CropActionButton>
                                <S.CropActionButton
                                  type="button"
                                  $primary
                                  disabled={
                                    !cropDrawing || cropDrawing.w < 10 || cropDrawing.h < 10
                                  }
                                  onClick={handleApplyCrop}
                                  data-testid="create-mem-apply-crop"
                                >
                                  Apply crop
                                </S.CropActionButton>
                              </S.CropActionRow>
                            </>
                          ) : (
                            renderImagePreviewControls({
                              showCropReplace: true,
                              previewUrl: imagePreview,
                            })
                          )}
                        </>
                      )}
                    </S.ImageSourceBody>
                  </S.ImageSourcePanel>

                  <S.ImageSourceDivider aria-hidden>
                    <S.ImageSourceOr>{intl.formatMessage(msgs.orSeparator)}</S.ImageSourceOr>
                    <S.ImageSourceDividerLine />
                  </S.ImageSourceDivider>

                  <S.ImageSourcePanel
                    $active={imageTab === 'ai'}
                    onClick={() => setImageTab('ai')}
                    data-testid="create-mem-tab-ai"
                  >
                    <S.ImageSourceTitle>{intl.formatMessage(msgs.aiImageTab)}</S.ImageSourceTitle>
                    <S.ImageSourceBody
                      $active={imageTab === 'ai'}
                      onClick={e => {
                        if (imageTab === 'ai') e.stopPropagation();
                      }}
                    >
                      <S.AiGenerateRow>
                        <S.AiPromptTextArea
                          value={aiPrompt}
                          onChange={e => setAiPrompt(e.target.value)}
                          placeholder={defaultAiPrompt}
                          data-testid="create-mem-ai-prompt"
                          rows={5}
                          disabled={isGeneratingAi || draftMemId !== null}
                        />
                        {!aiGeneratedImageUrl && !isGeneratingAi && (
                          <Button
                            buttonType="secondary"
                            buttonSize="medium"
                            onClick={handleGenerateAi}
                            disabled={!canGenerateAi}
                            data-testid="create-mem-generate-ai"
                          >
                            {intl.formatMessage(msgs.generateAiImage)}
                          </Button>
                        )}
                        {isGeneratingAi && (
                          <AiImageLoadingBar label={intl.formatMessage(msgs.generatingAiImage)} />
                        )}
                        {aiError && (
                          <S.AiError data-testid="create-mem-ai-error">{aiError}</S.AiError>
                        )}
                        {aiGeneratedImageUrl &&
                          renderImagePreviewControls({
                            showCropReplace: false,
                            previewUrl: aiGeneratedImageUrl,
                          })}
                      </S.AiGenerateRow>
                    </S.ImageSourceBody>
                  </S.ImageSourcePanel>
                </S.ImageSourceColumns>
              </S.ImageSectionInner>
            </S.ImageSectionGrid>
          </S.ImageSection>

          {submitError && (
            <S.SubmitError data-testid="create-mem-error">{submitError}</S.SubmitError>
          )}
          <S.ModalButtons>
            <Button
              buttonType="secondary"
              buttonSize="medium"
              onClick={handleClose}
              data-testid="create-mem-cancel-button"
            >
              {intl.formatMessage(sharedMemMessages.cancel)}
            </Button>
            {!isGeneratingAi && (
              <Button
                buttonType="primary"
                buttonSize="medium"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                loading={isSubmitting}
                data-testid="create-mem-submit-button"
              >
                {intl.formatMessage(msgs.submit)}
              </Button>
            )}
          </S.ModalButtons>
        </S.FormModal>
      </S.WideModal>
    </>
  );
};
